import { Route } from 'lucide-react';
import { SlideOut, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { WorkflowTraceList } from '../EventTypes/shared/index.js';
import { workflowTraceLabel, workflowTraceSteps } from '../../utils/workflowTrace.js';
import { shortEventId } from '../../utils/formatEventId.js';

/**
 * Workflow trace panel, opened from the ⋮ → Trace action.
 *
 * Mirrors the prototype's Trace drawer: title `Trace: {workflow name}`, the event
 * id as subtitle, and the step timeline underneath — see the live capture in
 * `e2e/prototype-trace-drawer.spec.js`.
 *
 * The empty state is the prototype's own copy. It is reachable only in theory:
 * the ⋮ item is gated on the event actually carrying steps
 * (`eventHasWorkflowTrace`). It exists so a caller that opens the panel without
 * checking explains itself instead of rendering a blank sheet.
 */
function TraceSlideOut({ event, isOpen, onClose }) {
  const { t } = useTranslation('work-queues-app');
  if (!event) return null;

  const steps = workflowTraceSteps(event);
  const { reference, name } = workflowTraceLabel(event);
  const title = name ? t('trace.panelTitle', { workflow: name }) : t('trace.panelTitleFallback');

  return (
    <SlideOut
      isOpen={isOpen}
      onClose={onClose}
      size="S"
      ariaLabel={title}
      renderHeader={(titleId) => (
        <SlideOut.Header
          titleId={titleId}
          size="S"
          icon={Route}
          title={title}
          subtitle={event.refId ?? shortEventId(event.id)}
          showClose
          onClose={onClose}
        />
      )}
    >
      {steps.length ? (
        <WorkflowTraceList steps={steps} reference={reference} title={name} section={false} />
      ) : (
        <StatusMessage
          icon={Route}
          title={t('trace.emptyTitle')}
          subtitle={t('trace.emptyDescription')}
        />
      )}
    </SlideOut>
  );
}

export default TraceSlideOut;
